import type { UserData, AppealsDashboardData } from './types'

interface PerformanceBarProps {
  userData: UserData
  perfData: AppealsDashboardData | null
  perfLoading: boolean
  onViewReport: () => void
}

export default function PerformanceBar({
  userData,
  perfData,
  perfLoading,
  onViewReport,
}: PerformanceBarProps) {
  if (!userData.loggedIn) return null

  if (perfLoading) {
    return (
      <div className="max-w-[1600px] mx-auto px-6 pt-6 pb-0">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm dark:bg-slate-800 dark:border-slate-700 p-5 animate-pulse">
          <div className="h-4 bg-slate-100 rounded w-56 mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-4">
                <div className="h-7 bg-slate-100 rounded w-12 mb-2" />
                <div className="h-3 bg-slate-100 rounded w-20" />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const lettersGenerated = userData.monthlyLmnCount || 0
  const hasAppealsData = perfData && (perfData.approved > 0 || perfData.denied > 0)
  const appealsWon = perfData?.approved || 0
  const appealsDenied = perfData?.denied || 0
  const winRate = hasAppealsData && (appealsWon + appealsDenied) > 0
    ? Math.round((appealsWon / (appealsWon + appealsDenied)) * 100)
    : null
  const timeSavedMinutes = lettersGenerated * 30
  const timeSavedDisplay = timeSavedMinutes >= 60
    ? `~${Math.round(timeSavedMinutes / 60)} hrs`
    : `~${timeSavedMinutes} min`

  return (
    <div className="max-w-[1600px] mx-auto px-6 pt-6 pb-0">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm dark:bg-slate-800 dark:border-slate-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
            <h3 className="text-sm font-bold text-slate-800">Practice Performance This Month</h3>
          </div>
          <button
            onClick={onViewReport}
            className="text-xs font-semibold text-teal-600 hover:text-teal-700 transition-colors flex items-center gap-1"
          >
            View Full Report
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {/* Letters Generated */}
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <p className="text-2xl font-bold text-slate-900">{lettersGenerated}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wide mt-1">Letters Generated</p>
          </div>

          {/* Appeals Won */}
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            {hasAppealsData ? (
              <>
                <p className="text-2xl font-bold text-slate-900">{appealsWon}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wide mt-1">Appeals Won</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-400 leading-snug">Track your first appeal</p>
                <button onClick={onViewReport} className="text-xs text-teal-600 font-semibold mt-1 hover:text-teal-700">
                  Go to Appeals &rarr;
                </button>
              </>
            )}
          </div>

          {/* Recovery Amount */}
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            {hasAppealsData ? (
              <>
                <p className="text-2xl font-bold text-emerald-600">&mdash;</p>
                <p className="text-xs text-slate-500 uppercase tracking-wide mt-1">Recovery Amount</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Add amounts in Appeals</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-400 leading-snug">See recovery metrics</p>
                <button onClick={onViewReport} className="text-xs text-teal-600 font-semibold mt-1 hover:text-teal-700">
                  Track Appeals &rarr;
                </button>
              </>
            )}
          </div>

          {/* Win Rate */}
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            {winRate !== null ? (
              <>
                <p className="text-2xl font-bold text-slate-900">{winRate}%</p>
                <p className="text-xs text-slate-500 uppercase tracking-wide mt-1">Win Rate</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{appealsWon}/{appealsWon + appealsDenied} decided</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-400 leading-snug">No outcomes yet</p>
                <p className="text-xs text-slate-400 mt-1">Mark appeal outcomes to track</p>
              </>
            )}
          </div>

          {/* Time Saved */}
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            {lettersGenerated > 0 ? (
              <>
                <p className="text-2xl font-bold text-slate-900">{timeSavedDisplay}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wide mt-1">Time Saved</p>
                <p className="text-[10px] text-slate-400 mt-0.5">~30 min per letter</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-400 leading-snug">Generate your first LMN to see time saved</p>
                <p className="text-xs text-slate-400 mt-1">~30 min saved per letter</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
